import React, { useContext, useEffect, useMemo, useState } from "react";
import { Mail, Phone, MessageSquare, Star, MoreVertical, CheckCircle2, Building2, Sun, MapPin, IdCard, Copy, User2, Info, Layers, Clock, PhoneCall, ShieldX } from "lucide-react";
import { Skeleton } from "@mui/material";
import InnerLoader from "../../../../component/ui/loader/inner-loader";
import Style from './style.module.css';
import Tooltip from "../../../../component/ui/tooltip";
import { GlobalContext } from "../../../../context/Context";
// --- Helper Components -----------------------------------------------------
const Chip = ({ label, tone = "info" }) => {
  const toneMap = {
    success: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    danger: "bg-rose-50 text-rose-700 ring-rose-200",
    warning: "bg-amber-50 text-amber-700 ring-amber-200",
    info: "bg-sky-50 text-sky-700 ring-sky-200",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${toneMap[tone]}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${tone === "success" ? "bg-emerald-500" : tone === "danger" ? "bg-rose-500" : tone === "warning" ? "bg-amber-500" : "bg-sky-500"
        }`} />
      {label}
    </span>
  );
};

const IconButton = ({ children, className = "", ...props }) => (
  <button
    className={`inline-flex h-10 w-10 items-center justify-center rounded-xl border border-solid border-slate-200 bg-white/90 text-slate-600 backdrop-blur transition hover:border-slate-300 hover:bg-white hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 ${className}`}
    {...props}
  >
    {children}
  </button>
);

const SectionCard = ({ title, right, children }) => (
  <section className="rounded-2xl border border-solid border-slate-200 bg-white shadow-sm">
    <header className="flex items-center justify-between px-5 py-4">
      <h3 className="text-sm font-semibold tracking-tight text-slate-800">{title}</h3>
      {right}
    </header>
    <div className="px-5 pb-5">
      {children}
    </div>
  </section>
);

const InfoItem = ({ icon, label, value, action }) => (
  <div className="group flex items-center gap-4 rounded-xl border border-solid border-slate-200 bg-white p-4 shadow-sm transition hover:shadow">
    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-50 text-slate-600">
      {icon}
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="truncate text-sm font-medium text-slate-900">{value || "—"}</div>
    </div>
    {action && <div className="opacity-0 transition group-hover:opacity-100">{action}</div>}
  </div>
);

const CopyBtn = ({ text }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => {
        try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1200); } catch { }
      }}
      className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium transition focus:outline-none ${copied ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}
      title="Copy"
    >
      <Copy className="h-3.5 w-3.5" /> {copied ? "Copied" : "Copy"}
    </button>
  );
};

const FieldRow = ({ label, value }) => (
  <div className="grid grid-cols-12 items-center gap-4 py-2">
    <div className="col-span-4 text-xs font-medium text-slate-500">{label}</div>
    <div className="col-span-8 text-sm text-slate-900">{value || "—"}</div>
  </div>
);

function FieldRowTable({ label, value }) {
  return (
    <div className="flex justify-between py-2 border-b border-solid border-slate-200 last:border-0">
      <span className="text-sm text-slate-700 font-medium">{label}</span>
      <span className="text-sm text-slate-500">{value}</span>
    </div>
  );
}

// --- Demo Data -------------------------------------------------------------
// const demo = {
//   id: "89001",
//   firstName: "Aadish",
//   lastName: "Kumar",
//   avatar: "https://images.unsplash.com/photo-1544006659-f0b21884ce1d?q=80&w=256&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTh8fG1hbnxlbnwwfHwwfHx8MA%3D%3D",
//   status: "out",
//   checkedText: "Checked out at 09:33 PM",
//   department: "Graphics & Multimedia",
//   shift: "Morning B1",
//   seat: "LD-F1-R2-S1",
//   email: "aadish@legendesk.com",
//   phone: "03032430361",
//   extension: "381",
//   reportingTo: "Hafsa Saleem Chughtai",
//   about: "",
//   skills: [],
//   joinDate: "2023-04-12",
//   employmentType: "Full-time",
//   office: "Karachi – HQ",
// };

// --- Main Component --------------------------------------------------------
export default function TeamProfileMain({ data, isLoading }) {
  let { state } = useContext(GlobalContext);

  const demo = {
    id: data?.employee_id,
    fullName: data?.name,
    firstName: data?.personal_info?.p_first_name,
    lastName: data?.personal_info?.p_last_name,
    avatar: data?.profile_picture,
    status: data?.attendance?.status,
    checkedText: data?.attendance?.checkedText,
    department: data?.department_name,
    shift: data?.company_info?.shift,
    seat: data?.company_info?.seating_location,
    email: data?.email,
    phone: data?.company_info?.mobile_number,
    extension: data?.company_info?.p_extension,
    reportingTo: data?.reporting_authority_name,
    about: data?.about,
    skills: data?.skills,
    tags: data?.tags,
    joinDate: data?.date_of_joining,
    employmentType: data?.emp_type,
    office: "Karachi - HQ",

    education: data?.educational_details?.data,
    workExperience: data?.experience_details?.data,
    family: data?.family_details,
  }
  const e = demo;
  const fullName = useMemo(() => `${e?.fullName}`, [e?.fullName]);

  const [officialInfo, setOfficialInfo] = useState({});
  const [aboutUs, setAboutUs] = useState('');
  const [skillSet, setSkillSet] = useState([]);
  const [tags, setTags] = useState([]);
  const [hierarchyInfo, setHierarchyInfo] = useState('');
  const [personalDetail, setPersonalDetail] = useState({});

  useEffect(() => {
    setOfficialInfo(data?.company_info);
    setAboutUs(data?.about);
    setSkillSet(data?.skill_set);
    setTags(data?.tags);
    setHierarchyInfo(data?.reporting_authority_name);
    setPersonalDetail(data?.personal_info);
  }, [data])

  return (
    <div className="bg-slate-50 text-slate-800 overflow-hidden overflow-y-auto">
      {/* Cover */}
      <div className="relative">
        <div className="relative h-48 w-full bg-gradient-to-r from-emerald-100 via-teal-100 to-sky-100 overflow-hidden">
          <img
            src={`${state.basePath}/cover-photo.jpg`}
            alt="cover"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
        </div>

        {/* Top right actions */}
        <div className="absolute right-4 top-4 flex items-center gap-2 z-10">
          <IconButton title="Favorite" aria-label="favorite"><Star className="h-5 w-5" /></IconButton>
          <a href={`tel:${e?.phone}`}><IconButton title="Call" aria-label="call"><PhoneCall className="h-5 w-5" /></IconButton></a>
          <a href={`mailto:${e?.email}`}><IconButton title="Email" aria-label="email"><Mail className="h-5 w-5" /></IconButton></a>
          <IconButton title="More" aria-label="more"><MoreVertical className="h-5 w-5" /></IconButton>
        </div>

        {/* Identity strip */}
        <div className="mx-auto -mt-10 w-full max-w-7xl px-4">
          <div className="flex flex-col gap-4 rounded-2xl border border-solid border-slate-200 bg-white/100 p-4 shadow backdrop-blur md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4">
              {isLoading ? (
                e?.avatar ? (
                  <img
                    src={e.avatar}
                    alt={fullName}
                    className="h-[160px] w-[160px] mt-[-80px] rounded-full ring-4 ring-white object-cover"
                  />
                ) : (
                  <span className="h-[160px] w-[160px] mt-[-80px] rounded-full ring-4 ring-white bg-slate-200 text-slate-700 flex items-center justify-center text-[52px] font-semibold">
                    {fullName
                      ?.split(' ')
                      .map(w => w[0])
                      .slice(0, 2)
                      .join('')
                      .toUpperCase()}
                  </span>
                )
              ) : (
                <div className="h-[160px] w-[160px] mt-[-80px] rounded-full ring-4  bg-white relative ring-white">
                  <div className="w-full h-full absolute top-0 bottom-0 left-0 right-0 m-auto ring-white bg-gray-200 animate-pulse rounded-full ring-4" />
                </div>
              )}
              <div>
                {isLoading ? (
                  <>
                    <div className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                      <span>{e?.id} – {fullName}</span>
                      {
                        (data?.employment_status === 'Active') ? (
                          <Tooltip content={'Active'} position={'top center'}><CheckCircle2 className="h-4 w-4 text-emerald-500" /></Tooltip>
                        ) : (
                          <Tooltip content={'In Active'} position={'top center'}><ShieldX className="h-4 w-4 text-red-500" /></Tooltip>
                        )
                      }
                      {
                        (e?.status) ? (
                          <Chip label={e?.status} tone={e?.status === "out" ? "warning" : "success"} />
                        ) : null
                      }
                    </div>
                    <div className="mt-1 text-xs text-slate-500">{e?.checkedText}</div>
                    <div className="text-xs text-slate-500">{e?.department}</div>
                  </>
                ) : (
                  <>
                    <div className="h-4 w-40 bg-gray-200 rounded animate-pulse mb-1"></div>
                    <div className="h-3 w-24 bg-gray-200 rounded animate-pulse mb-1"></div>
                    <div className="h-3 w-20 bg-gray-200 rounded animate-pulse"></div>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 md:pr-2">
              {isLoading ? (
                <div className="rounded-xl border border-solid border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                  <div className="flex items-center gap-2"><User2 className="h-4 w-4" /><span>Reporting To</span></div>
                  <div className="mt-0.5 font-medium text-slate-900">{e?.reportingTo}</div>
                </div>
              ) : (
                <div className="h-10 w-32 bg-gray-200 rounded animate-pulse"></div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <main className="mx-auto mt-6 w-full max-w-7xl space-y-6 px-4 pb-20">
        {/* Quick facts grid */}
        {
          (isLoading) ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <InfoItem
                icon={<Building2 className="h-5 w-5" />}
                label="Department"
                value={officialInfo?.department}
              />
              <InfoItem
                icon={<Sun className="h-5 w-5" />}
                label="Shift"
                value={officialInfo?.shift}
              />
              <InfoItem
                icon={<MapPin className="h-5 w-5" />}
                label="Seating Location"
                value={officialInfo?.seating_location}
              />
              <InfoItem
                icon={<Mail className="h-5 w-5" />}
                label="Official Email"
                value={<a className="hover:underline" href={`mailto:${e?.email}`}>{e?.email}</a>}
                action={<CopyBtn text={officialInfo?.official_email} />}
              />
              <InfoItem
                icon={<Phone className="h-5 w-5" />}
                label="Mobile Number"
                value={<a className="hover:underline" href={`tel:${e?.phone}`}>{e?.phone}</a>}
                action={<CopyBtn text={officialInfo?.mobile_number} />}
              />
              <InfoItem
                icon={<IdCard className="h-5 w-5" />}
                label="Extension"
                value={officialInfo?.p_extension}
              />
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {
                Array.from({ length: 6 }).map((_, i) => {
                  return (
                    <Skeleton style={{ width: '100%', height: '72px' }} key={i} />
                  )
                })

              }
            </div>
          )
        }

        {/* About */}
        <SectionCard title="About" right={<button className="inline-flex items-center gap-1 rounded-lg border border-solid border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"><Info className="h-4 w-4" /> Edit</button>}>
          {
            (isLoading) ? (
              (aboutUs && aboutUs?.length) ? (
                <p className="text-sm leading-relaxed text-slate-700">{aboutUs}</p>
              ) : (
                <div className="flex items-center justify-between rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6">
                  <p className="text-sm text-slate-500">No About Found</p>
                  <button className={`${Style.loadMoreBtn} rounded-lg px-3 py-1.5 text-xs font-medium text-white`}>Add About</button>
                </div>
              )
            ) : <InnerLoader />
          }
        </SectionCard>

        {/* Skill Set */}
        <SectionCard title="Skill Set" right={<button className="inline-flex items-center gap-1 rounded-lg border border-solid border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"><Layers className="h-4 w-4" /> Manage</button>}>
          {
            (isLoading) ? (
              skillSet?.length ? (
                <div className="flex flex-wrap gap-2">
                  {skillSet?.map((s, i) => (
                    <span key={i} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">{s}</span>
                  ))}
                  <button class="rounded-full border border-dashed border-slate-300 px-3 py-1 text-xs text-slate-500 hover:bg-slate-50">+ Add</button>
                </div>
              ) : (
                <div className="flex items-center justify-between rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6">
                  <p className="text-sm text-slate-500">No Skill Set Found</p>
                  <button className={`${Style.loadMoreBtn} rounded-lg px-3 py-1.5 text-xs font-medium text-white`}>Add Skills</button>
                </div>
              )
            ) : <InnerLoader />
          }
        </SectionCard>

        {/* TAGs */}
        <SectionCard title="Tag" right={<button className="inline-flex items-center gap-1 rounded-lg border border-solid border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"><Layers className="h-4 w-4" /> Manage</button>}>
          {
            (isLoading) ? (
              tags?.length ? (
                <div className="flex flex-wrap gap-2">
                  {tags?.map((s, i) => (
                    <span key={i} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">{s}</span>
                  ))}

                  <button class="rounded-full border border-dashed border-slate-300 px-3 py-1 text-xs text-slate-500 hover:bg-slate-50">+ Add</button>
                </div>
              ) : (
                <div className="flex items-center justify-between rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6">
                  <p className="text-sm text-slate-500">No Tag Found</p>
                  <button className={`${Style.loadMoreBtn} rounded-lg px-3 py-1.5 text-xs font-medium text-white`}>Add TAgs</button>
                </div>
              )
            ) : <InnerLoader />
          }
        </SectionCard>

        {/* Basic Information */}
        <SectionCard title="Basic Information">
          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <FieldRow label="Employee ID" value={e?.id} />
              <FieldRow label="First Name" value={e?.firstName} />
              <FieldRow label="Last Name" value={e?.lastName} />
            </div>
            <div>
              <FieldRow label="Email Address" value={<a className="hover:underline" href={`mailto:${e?.email}`}>{e?.email}</a>} />
              <FieldRow label="Phone" value={<a className="hover:underline" href={`tel:${e?.phone}`}>{e?.phone}</a>} />
            </div>
          </div>
        </SectionCard>

        {/* Work Information */}
        <SectionCard title="Work Information">
          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <FieldRow value={data?.department_name} label={"Department"} />
              <FieldRow value={officialInfo?.seating_location} label={"Location"} />
              <FieldRow value={data?.designation_name} label={"Designation"} />
              <FieldRow value={hierarchyInfo} label={"Reporting Manager"} />
            </div>

            <div>
              <FieldRow value={data?.emp_type} label={"Employment Type"} />
              <FieldRow value={data?.employment_status} label={"Employee Status"} />
              <FieldRow value={"Direct"} label={"Source of Hire"} />
              <FieldRow value={data?.date_of_joining} label={"Date of Joining"} />
              <FieldRow value={data?.experience_count} label={"Total Experience"} />
            </div>
          </div>
        </SectionCard>

        {/* Personal Details */}
        <SectionCard title="Personal Details">
          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <FieldRow value={personalDetail?.date_of_birth} label={"Date of Birth"} />
              <FieldRow value={personalDetail?.place_of_birth} label={"Place of Birth"} />
              <FieldRow value={personalDetail?.gender} label={"Gender"} />
              <FieldRow value={personalDetail?.religion} label={"Religion"} />
              <FieldRow value={personalDetail?.nationality} label={"Nationality"} />
              <FieldRow value={personalDetail?.marital_status} label={"Marital Status"} />
            </div>

            <div>
              <FieldRow value={personalDetail?.residential_address} label={"Residential Address"} />
              <FieldRow value={personalDetail?.permanent_address} label={"Permanent Address"} />
              <FieldRow value={personalDetail?.cnic} label={"CNIC"} />
              <FieldRow value={personalDetail?.cnic_issue_date} label={"CNIC Issue Date"} />
              <FieldRow value={personalDetail?.expiry_date} label={"CNIC Expiry Date"} />
            </div>
          </div>
        </SectionCard>

        {/* Contact Details */}
        <SectionCard title="Contact Details">
          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <FieldRow label={"Work Phone Number"} value={officialInfo?.mobile_number} />
              <FieldRow label={"Extension"} value={officialInfo?.p_extension} />
              <FieldRow label={"Seating Location"} value={officialInfo?.seating_location} />
              <FieldRow label={"Email"} value={data?.email} />
              <FieldRow label={"Present Address"} value={personalDetail?.residential_address} />
              <FieldRow label={"Permanent Address"} value={personalDetail?.permanent_address} />
            </div>

            <div>
              <FieldRow label={"Ask me about / Expertise"} value={"-"} />
            </div>
          </div>
        </SectionCard>

        {/* EDUCATION + EXPERIENCE */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <SectionCard title="Education">
            {
              (isLoading) ? (
                (e?.education?.length) ? (
                  <div className="space-y-3">
                    {
                      e?.education?.map((edu, i) => {
                        const name = Object.entries(edu).find(([k]) =>
                          k.includes('_institute_name')
                        )?.[1] || '';

                        const title = Object.entries(edu).find(([k]) =>
                          k.includes('_degree_title')
                        )?.[1] || '';

                        const majors = Object.entries(edu).find(([k]) =>
                          k.includes('_degree_majors')
                        )?.[1] || '';
                        const yearpassing = Object.entries(edu).find(([k]) =>
                          k.includes('_year_of_passing')
                        )?.[1] || '';

                        return (
                          <div key={i} className="flex items-start gap-3">
                            <span className="mt-1 inline-block h-2 w-2 rounded-full bg-gray-300" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">{`${title} ${(majors) ? `- ${majors}` : null} (${yearpassing})`}</div>
                              <div className="text-sm text-gray-600">{name}</div>
                            </div>
                          </div>
                        )
                      })
                    }
                  </div>
                ) : (
                  <div className="flex items-center justify-between rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6">
                    <p className="text-sm text-slate-500">No Education Found</p>
                    <button className={`${Style.loadMoreBtn} rounded-lg px-3 py-1.5 text-xs font-medium text-white`}>Add Education</button>
                  </div>
                )
              ) : (
                <InnerLoader />
              )
            }


          </SectionCard>

          <SectionCard title="Work Experience">
            {
              (isLoading) ? (
                (e?.workExperience?.length) ? (
                  <ol className="relative ml-3 space-y-5">
                    {e?.workExperience?.map((edu, idx) => {
                      const designation = Object.entries(edu).find(([k]) =>
                        k.includes('_designation')
                      )?.[1] || '';

                      const department = Object.entries(edu).find(([k]) =>
                        k.includes('_department')
                      )?.[1] || '';

                      const servicee_duration = Object.entries(edu).find(([k]) =>
                        k.includes('_servicee_duration')
                      )?.[1] || '';

                      const company_name = Object.entries(edu).find(([k]) =>
                        k.includes('_company_name')
                      )?.[1] || '';

                      return (
                        <li key={idx} className="pl-5">
                          <span className="absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full bg-gray-300" />
                          <div className="text-sm font-medium text-gray-900">
                            {(designation === '-') ? (department === '-') ? null : department : designation} <span className="text-gray-500">— {company_name}</span>
                          </div>
                          <div className="text-xs text-gray-500">{servicee_duration}</div>
                        </li>
                      )
                    })}
                  </ol>
                ) : (
                  <div className="flex items-center justify-between rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6">
                    <p className="text-sm text-slate-500">No Work Experience Found</p>
                    <button className={`${Style.loadMoreBtn} rounded-lg px-3 py-1.5 text-xs font-medium text-white`}>Add Work Experience</button>
                  </div>
                )
              ) : (
                <InnerLoader />
              )
            }

          </SectionCard>
        </div>

        {/* EDUCATION TABLE */}
        <SectionCard title="Education">
          {
            isLoading ? (
              e?.education?.length ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full border-separate border-spacing-0 rounded-xl text-sm">
                    <thead>
                      <tr className="bg-[#F5F7FA] text-left">
                        <th className="px-4 py-3 font-medium">Degree</th>
                        <th className="px-4 py-3 font-medium">Majors</th>
                        <th className="px-4 py-3 font-medium">Institute</th>
                        <th className="px-4 py-3 font-medium">Year of Passing</th>
                      </tr>
                    </thead>
                    <tbody>
                      {e?.education?.map((edu, i) => {
                        const institute = Object.entries(edu).find(([k]) =>
                          k.includes('_institute_name')
                        )?.[1] || '';
                        const degree = Object.entries(edu).find(([k]) =>
                          k.includes('_degree_title')
                        )?.[1] || '';
                        const majors = Object.entries(edu).find(([k]) =>
                          k.includes('_degree_majors')
                        )?.[1] || '';
                        const year = Object.entries(edu).find(([k]) =>
                          k.includes('_year_of_passing')
                        )?.[1] || '';

                        return (
                          <tr key={i} className="border-b even:bg-[#f8fafc] hover:bg-[#f0f7ff]">
                            <td className="px-4 py-4 font-medium">{degree}</td>
                            <td className="px-4 py-4">{majors}</td>
                            <td className="px-4 py-4">{institute}</td>
                            <td className="px-4 py-4">{year}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex items-center justify-between rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6">
                  <p className="text-sm text-slate-500">No Education Found</p>
                  <button className={`${Style.loadMoreBtn} rounded-lg px-3 py-1.5 text-xs font-medium text-white`}>Add Education</button>
                </div>
              )
            ) : (
              <InnerLoader />
            )
          }
        </SectionCard>

        {/* WORK EXPERIENCE TABLE */}
        <SectionCard title="Work Experience">
          {
            isLoading ? (
              e?.workExperience?.length ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full border-separate border-spacing-0 rounded-xl text-sm">
                    <thead>
                      <tr className="bg-[#F5F7FA] text-left">
                        <th className="px-4 py-4 font-medium">Designation</th>
                        <th className="px-4 py-4 font-medium">Department</th>
                        <th className="px-4 py-4 font-medium">Company</th>
                        <th className="px-4 py-4 font-medium">Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {e?.workExperience?.map((exp, idx) => {
                        const designation = Object.entries(exp).find(([k]) =>
                          k.includes('_designation')
                        )?.[1] || '';
                        const department = Object.entries(exp).find(([k]) =>
                          k.includes('_department')
                        )?.[1] || '';
                        const duration = Object.entries(exp).find(([k]) =>
                          k.includes('_servicee_duration')
                        )?.[1] || '';
                        const company = Object.entries(exp).find(([k]) =>
                          k.includes('_company_name')
                        )?.[1] || '';

                        return (
                          <tr key={idx} className="border-b even:bg-[#f8fafc] hover:bg-[#f0f7ff]">
                            <td className="px-4 py-3 font-medium">{designation}</td>
                            <td className="px-4 py-3 font-medium">{department}</td>
                            <td className="px-4 py-3">{company}</td>
                            <td className="px-4 py-3">{duration}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex items-center justify-between rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6">
                  <p className="text-sm text-slate-500">No Work Experience Found</p>
                  <button className={`${Style.loadMoreBtn} rounded-lg px-3 py-1.5 text-xs font-medium text-white`}>Add Work Experience</button>
                </div>
              )
            ) : (
              <InnerLoader />
            )
          }
        </SectionCard>

        {/* Family Details */}
        <SectionCard title="Family Details">
          {
            (isLoading) ? (
              (e?.family?.length) ? (
                <div className="divide-y divide-gray-100">
                  {e?.family?.map((f, idx) => (
                    <details key={idx} className="group">
                      <summary className="flex cursor-pointer list-none items-center justify-between py-3">
                        <span className="text-sm font-medium text-gray-900">{f?.dependent_relation}: {f?.dependent_name}</span>
                        <svg className="h-5 w-5 text-gray-500 transition-transform group-open:rotate-180" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" /></svg>
                      </summary>
                      <div className="grid grid-cols-1 gap-3 pb-4 sm:grid-cols-3">
                        <KeyValue label="Relationship" value={f?.dependent_relation ?? '-'} compact />
                        <KeyValue label="DOB" value={f?.dependent_dob ?? '-'} compact />
                        <KeyValue label="Phone" value={(f?.dependent_phone ?? '-')} compact />
                      </div>
                    </details>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-between rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6">
                  <p className="text-sm text-slate-500">No Family Details Found</p>
                  <button className={`${Style.loadMoreBtn} rounded-lg px-3 py-1.5 text-xs font-medium text-white`}>Add Family Details</button>
                </div>
              )
            ) : (
              <InnerLoader />
            )
          }
        </SectionCard>

        {/* Footer CTA */}
        <div className="sticky bottom-4 z-10 mx-auto max-data, isLoading rounded-2xl border border-solid border-slate-200 bg-white/90 p-3 shadow backdrop-blur">
          <div className="flex flex-col items-center justify-between gap-3 md:flex-row">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Clock className="h-4 w-4" />
              <span className="hidden sm:inline">Last activity</span>
              <span className="font-medium text-slate-900">{e?.checkedText}</span>
            </div>
            <div className="flex items-center gap-2">
              <a href={`tel:${e?.phone}`} className="inline-flex items-center gap-2 rounded-xl bg-[#2563eb] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#1e4fdb] focus:outline-none focus-visible:ring-2 focus-visible:[#1e4fdb]">
                <Phone className="h-4 w-4" /> Call
              </a>
              <a href={`mailto:${e?.email}`} className="inline-flex items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50">
                <Mail className="h-4 w-4" /> Email
              </a>
              <button className="inline-flex items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50">
                <MessageSquare className="h-4 w-4" /> Message
              </button>
            </div>
          </div>
        </div>
      </main >
    </div >
  );
}

// export default TeamProfileMain
const KeyValue = ({ label, value, type, compact = false }) => {
  const v =
    type === "email" ? (
      <a className="hover:underline" href={`mailto:${value}`}>{value}</a>
    ) : (
      value
    );
  return (
    <div>
      <div className={`mb-0.5 ${compact ? "text-[11px]" : "text-xs"} font-semibold uppercase tracking-wide text-gray-500`}>
        {label}
      </div>
      <div className={`${compact ? "text-sm" : "text-base"} text-gray-900`}>{v || "—"}</div>
    </div>
  );
};